import { AtoN } from '@ais-ui/api-spec/config';
import { useLocalization } from '@ais-ui/locale-management';
import React from 'react';
import {
  AccordionItem,
  AccordionTrigger,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';

import { cn } from '@ais-ui/utils';
import { PiDiamondFill, PiTableThin } from 'react-icons/pi';

import {
  DisabledShowAtonButton,
  EnabledShowAtonButton,
} from './ShowAtonButton';
import { AtonItemContent } from './AtonItemContent';
import {
  DisabledPhysicalDropdownMenu,
  EnabledPhysicalDropdownMenu,
} from './PhysicalDropdownMenu';

interface PhysicalAtonItemProps {
  aton: AtoN;
  requiresMap: boolean;
}

interface PhysicalAtonItemContentProps {
  aton: AtoN;
}

const PhysicalAtonItemContent = ({ aton }: PhysicalAtonItemContentProps) => {
  return (
    <div className="pb-4">
      <SubscriberTable aton={aton}></SubscriberTable>
      <div className="flex px-4 space-x-2">
        <p className="text-muted-foreground">State</p>
        <p>{aton.state === 1 ? 'on position' : 'off position'}</p>
      </div>
    </div>
  );
};

interface SubscriberTableProps {
  aton: AtoN;
}

const SubscriberTable = ({ aton }: SubscriberTableProps) => {
  const { messages } = useLocalization();

  return (
    <div className="pb-4">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" className="space-x-2">
            <PiTableThin className="h-6 w-6" />
            <p className="">{messages.atonPhysicalSubscriberTableTitle}</p>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full" side="left">
          <ScrollArea className="h-40 w-full py-2 overflow-auto pl-2 pr-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {messages.atonPhysicalSubscriberTableIdHeader}
                  </TableHead>
                  <TableHead>
                    {messages.atonPhysicalSubscriberTableNameHeader}
                  </TableHead>
                  <TableHead>
                    {messages.atonPhysicalSubscriberTableEmailHeader}
                  </TableHead>
                  <TableHead className="text-right">
                    {messages.atonPhysicalSubscriberTablePhoneHeader}
                  </TableHead>
                  <TableHead>
                    {messages.atonPhysicalSubscriberTableUrlHeader}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {aton.alarmSetup?.map((alarm, index) => (
                  <TableRow key={alarm.name || index}>
                    <TableCell className="font-medium">{index + 1}</TableCell>
                    <TableCell>{alarm.name}</TableCell>
                    <TableCell>{alarm.email}</TableCell>
                    <TableCell className="text-right">{alarm.sms}</TableCell>
                    <TableCell>{alarm.url}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const PhysicalAtonItem = ({
  aton,
  requiresMap,
}: PhysicalAtonItemProps) => {
  const { messages } = useLocalization();

  return (
    <AccordionItem
      key={aton.uuid}
      value={aton.uuid as string}
      className={cn('pr-4 pl-2')}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <div className="flex space-x-1 items-center">
            {requiresMap && (
              <EnabledShowAtonButton aton={aton}></EnabledShowAtonButton>
            )}
            {!requiresMap && <DisabledShowAtonButton></DisabledShowAtonButton>}
            <Separator orientation="vertical" className="h-6 bg-gray-300" />
          </div>

          <AccordionTrigger className="w-[205px]" hideChevron={true}>
            <div className="flex ml-4 space-x-2 items-center">
              {aton.virtual === 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <PiDiamondFill
                        className={cn(
                          'h-4 w-4',
                          aton.state === 1 && 'text-green-500',
                          aton.state === 2 && 'text-yellow-500',
                          aton.state === 3 && 'text-red-500'
                        )}
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {aton.state === 0 && 'Unknown state'}
                    {aton.state === 1 && 'On position'}
                    {aton.state === 2 && 'Off position (short duration)'}
                    {aton.state === 3 && 'Off position (extended duration)'}
                  </TooltipContent>
                </Tooltip>
              )}
              <div className="ml-4 space-y-1">
                <p className="text-sm text-left font-medium leading-none group-hover:underline">
                  {aton.name}
                </p>
              </div>
            </div>
          </AccordionTrigger>
        </div>
        {requiresMap && <EnabledPhysicalDropdownMenu aton={aton} />}
        {!requiresMap && (
          <DisabledPhysicalDropdownMenu
            aton={aton}
          ></DisabledPhysicalDropdownMenu>
        )}
      </div>

      <AtonItemContent aton={aton}>
        <PhysicalAtonItemContent aton={aton}></PhysicalAtonItemContent>
      </AtonItemContent>
    </AccordionItem>
  );
};
